import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Package, Check, Truck, Home, XCircle, ArrowLeft, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

const ORDER_STATUSES = [
  { id: "order_received", label: "Rendelés felvéve", icon: Package, description: "Megkaptuk a rendelést" },
  { id: "payment_pending", label: "Fizetés folyamatban", icon: Clock, description: "Várakozás a fizetésre" },
  { id: "paid", label: "Fizetett", icon: Check, description: "Fizetés sikeres" },
  { id: "preparing", label: "Rendelés készülőben", icon: Package, description: "Készítjük a rendelést" },
  { id: "completed", label: "Átvette", icon: Home, description: "Sikeres átvétel" }
];

export default function OrderTracking() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrder();
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderNumber}/track`);
      setOrder(response.data);
    } catch (err) {
      setError("Rendelés nem található");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('hu-HU').format(price) + ' Ft';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: "Készpénz átvételkor",
      transfer: "Banki átutalás",
      card_in_person: "Bankkártyás fizetés személyesen"
    };
    return labels[method] || method;
  };

  const getCurrentStatusIndex = () => {
    if (!order) return -1;
    if (order.status === "cancelled" || order.status === "payment_failed") return -1;
    return ORDER_STATUSES.findIndex(s => s.id === order.status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Betöltés...</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Rendelés nem található</h1>
          <p className="text-white/60 mb-6">Ellenőrizd a rendelésszámot</p>
          <button
            onClick={() => navigate("/shop")}
            className="text-[#D4AF37] hover:underline"
          >
            Vissza a shopba
          </button>
        </div>
      </div>
    );
  }

  const currentStatusIndex = getCurrentStatusIndex();
  const isCancelled = order.status === "cancelled";
  const isPaymentFailed = order.status === "payment_failed";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#141414] border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate("/shop")}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft size={24} />
          </button>
          <img src={LOGO_URL} alt="Gepárd" className="h-8" />
          <span className="text-white font-semibold">Rendelés követése</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Order Number */}
        <div className="text-center mb-8">
          <p className="text-white/60 text-sm mb-1">Rendelésszám</p>
          <h1 className="text-2xl font-bold text-[#D4AF37]">{order.order_number}</h1>
          <p className="text-white/40 text-sm mt-2">Leadva: {formatDate(order.created_at)}</p>
        </div>

        {/* Status Timeline */}
        {isCancelled ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 mb-8 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-red-500 mb-2">Rendelés törölve</h2>
            <p className="text-white/60">Ez a rendelés törölve lett.</p>
          </div>
        ) : isPaymentFailed ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 mb-8 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-red-500 mb-2">Fizetés sikertelen</h2>
            <p className="text-white/60">A fizetési határidő lejárt. Kérjük, vedd fel velünk a kapcsolatot.</p>
          </div>
        ) : (
          <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-6 mb-8">
            <h2 className="text-white font-semibold mb-6">Rendelés állapota</h2>
            
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-white/10" />
              <div 
                className="absolute left-6 top-0 w-0.5 bg-[#D4AF37] transition-all duration-500"
                style={{ 
                  height: currentStatusIndex >= 0 
                    ? `calc(${(currentStatusIndex / (ORDER_STATUSES.length - 1)) * 100}% + 12px)` 
                    : '0%' 
                }}
              />
              
              {/* Status Steps */}
              <div className="space-y-8">
                {ORDER_STATUSES.map((status, index) => {
                  const isCompleted = index <= currentStatusIndex;
                  const isCurrent = index === currentStatusIndex;
                  const StatusIcon = status.icon;
                  
                  return (
                    <div key={status.id} className="relative flex items-start gap-4">
                      <div 
                        className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                          isCompleted 
                            ? "bg-[#D4AF37] text-[#0A0A0A]" 
                            : "bg-[#0A0A0A] text-white/30 border border-white/10"
                        } ${isCurrent ? "ring-4 ring-[#D4AF37]/30" : ""}`}
                      >
                        <StatusIcon size={20} />
                      </div>
                      <div className="pt-2">
                        <p className={`font-medium ${isCompleted ? "text-white" : "text-white/40"}`}>
                          {status.label}
                        </p>
                        <p className={`text-sm ${isCompleted ? "text-white/60" : "text-white/30"}`}>
                          {status.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-6 mb-8">
          <h2 className="text-white font-semibold mb-4">Rendelt termékek</h2>
          
          <div className="space-y-3">
            {order.items?.map((item, index) => (
              <div key={index} className="flex items-center gap-4 bg-[#0A0A0A] p-3 rounded-lg">
                {item.image_url && (
                  <div className="w-16 h-16 bg-[#141414] rounded overflow-hidden flex-shrink-0">
                    <img
                      src={item.image_url.startsWith("/api/") ? `${BACKEND_URL}${item.image_url}` : item.image_url}
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{item.product_name}</p>
                  {Object.keys(item.variation_selections || {}).length > 0 && (
                    <p className="text-white/50 text-sm">
                      {Object.entries(item.variation_selections).map(([k, v]) => `${k}: ${v}`).join(", ")}
                    </p>
                  )}
                  <p className="text-white/60 text-sm">{item.quantity} db × {formatPrice(item.price)}</p>
                </div>
                <p className="text-[#D4AF37] font-bold">{formatPrice(item.price * item.quantity)}</p>
              </div>
            ))}
          </div>
          
          <div className="border-t border-white/10 mt-4 pt-4 flex justify-between items-center">
            <span className="text-white font-semibold">Összesen:</span>
            <span className="text-2xl font-bold text-[#D4AF37]">{formatPrice(order.total_price)}</span>
          </div>
        </div>

        {/* Payment Info */}
        <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-6">
          <h2 className="text-white font-semibold mb-4">Fizetési információ</h2>
          <p className="text-[#D4AF37]">{getPaymentMethodLabel(order.payment_method)}</p>
          
          {order.payment_method === "transfer" && order.status === "pending" && (
            <div className="mt-4 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
              <p className="text-white text-sm">
                <strong>Fontos:</strong> Kérjük, 24 órán belül utalja át az összeget az emailben megadott számlaszámra.
                A közleménybe írd be a rendelésszámot: <strong>{order.order_number}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className="text-center mt-8 text-white/40 text-sm">
          <p>Kérdésed van? Keress minket!</p>
          <p className="text-[#D4AF37]">+36 30 624 4757 | gepardlovasegyesulet@gmail.com</p>
        </div>
      </main>
    </div>
  );
}
